// Smart inference of the scrapbook recap headline + subtitle.
//
// Per the user spec: each night's recap card should feel hand-picked. We
// derive a punchy display headline + a Caveat-style subtitle from the night's
// data (drink totals, venue count, day-of-week, solo vs squad). Pure +
// deterministic so the same night always produces the same headline — that
// matters for caching, re-rendering, and visual goldens.
//
// Inputs intentionally read from the same RecapMetadata struct the canvas
// renderer consumes — no extra API calls.

export type SmartTitleInputs = {
  nightDate: Date
  venueCount: number
  drinkTotals: { beer: number; wine: number; cocktail: number; shot: number }
  primaryVenueName?: string | null
  participants?: string[] // post-MVP squads; [] or undefined = solo
  squadName?: string | null
}

export type SmartTitle = {
  /** Display headline — Bagel Fat One, lowercased + trailing period. */
  headline: string
  /** Caveat handwriting subtitle — sentence case, witty. */
  subtitle: string
  /** Orange stamp accent — short word ("legendary" / "epic" / "claimed"). */
  stamp: string
}

const DAY_OF_WEEK: readonly string[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]

/**
 * Pick a headline / subtitle / stamp word based on the night's shape.
 *
 * Decision order (first match wins):
 *   1. shots-dominated      → "tour de shot."   / "the head was loud."
 *   2. wine-dominated       → "long live wine." / "earned brunch."
 *   3. squad night          → "team {name} claimed {N} bars."
 *   4. solo (no friends)    → "alone but legendary."
 *   5. single venue, long   → "long live {venue}."
 *   6. multi-stop, day-of   → "{N}-stop {dayOfWeek}."
 *   7. fallback             → "tour de bar." / "absolutely earned brunch."
 */
export function inferSmartTitle(inputs: SmartTitleInputs): SmartTitle {
  const { nightDate, venueCount, drinkTotals, primaryVenueName, participants, squadName } = inputs
  const totalDrinks = drinkTotals.beer + drinkTotals.wine + drinkTotals.cocktail + drinkTotals.shot
  const dayName = DAY_OF_WEEK[nightDate.getDay()] ?? 'night'
  const stops = Math.max(venueCount, 1)
  const isSquad = (participants?.length ?? 0) > 0 || !!squadName

  // (1) shots-dominated — at least 2 shots AND shots ≥ 40% of all drinks.
  if (drinkTotals.shot >= 2 && drinkTotals.shot * 5 >= totalDrinks * 2) {
    return {
      headline: 'tour de shot.',
      subtitle: 'the head was loud.',
      stamp: 'legendary',
    }
  }

  // (2) wine-dominated — wine ≥ 50% of drinks AND ≥ 2 wines.
  if (drinkTotals.wine >= 2 && drinkTotals.wine * 2 >= totalDrinks) {
    return {
      headline: 'long live wine.',
      subtitle: 'absolutely earned brunch.',
      stamp: 'epic',
    }
  }

  // (3) squad — N+ people, stamp the team.
  if (isSquad) {
    const teamPiece = squadName ? `team ${lower(squadName)}` : 'the squad'
    return {
      headline: `${teamPiece} claimed ${stops} bar${stops === 1 ? '' : 's'}.`,
      subtitle: 'stats don’t lie.',
      stamp: 'claimed',
    }
  }

  // (4) solo + meaningful night.
  // Only fires when the caller explicitly passed `participants: []` (an
  // affirmative "I went out alone"). When `participants` is undefined we
  // can't distinguish "solo" from "data missing", so we fall through.
  if (participants !== undefined && participants.length === 0 && totalDrinks >= 3) {
    // Solo with multiple stops → "alone but legendary."
    if (stops >= 2) {
      return {
        headline: 'alone but legendary.',
        subtitle: 'no audience required.',
        stamp: 'legendary',
      }
    }
  }

  // (5) single venue with name → "long live {venue}."
  if (stops === 1 && primaryVenueName && primaryVenueName.trim().length > 0) {
    const short = shortenForHeadline(primaryVenueName)
    return {
      headline: `long live ${lower(short)}.`,
      subtitle: 'one stop, no regrets.',
      stamp: 'legendary',
    }
  }

  // (6) multi-stop with the day-of-week.
  if (stops >= 2) {
    return {
      headline: `${stops}-stop ${dayName}.`,
      subtitle: 'absolutely earned brunch.',
      stamp: 'epic',
    }
  }

  // (7) fallback — the canon default.
  return {
    headline: 'tour de bar.',
    subtitle: 'absolutely earned brunch.',
    stamp: 'legendary',
  }
}

function lower(s: string): string {
  return s.toLowerCase()
}

/**
 * Trim a venue name down so it fits in a single-line display headline. Strips
 * common prefixes ("The "), drops everything after a comma, then caps length.
 */
export function shortenForHeadline(name: string): string {
  const noPrefix = name.replace(/^the\s+/i, '')
  const noTail = noPrefix.split(',')[0] ?? noPrefix
  const trimmed = noTail.trim()
  if (trimmed.length <= 18) return trimmed
  return `${trimmed.slice(0, 17).trimEnd()}…`
}
